import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Table,
  Badge,
  Button,
  Form,
  Row,
  Col,
  InputGroup,
  Dropdown,
  Spinner,
  Pagination,
} from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { adminService } from "../../services/adminService";
import BookingDetailsModal from "./BookingDetailsModal";
import AddBookingModal from "./AddBookingModal";
import CancellationReasonModal from "./CancellationReasonModal";
import ManualRefundModal from "./ManualRefundModal";
import DateRangeFilter, {
  PeriodValue,
} from "../../components/Common/DateRangeFilter";
import toast from "react-hot-toast";

const toNum = (v: any) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const fmt = (v: any) =>
  `₹${toNum(v).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

interface AmountBreakdownPopoverProps {
  booking: any;
  anchorRect: DOMRect | null;
}

const AmountBreakdownPopover: React.FC<AmountBreakdownPopoverProps> = ({
  booking,
  anchorRect,
}) => {
  const nestedPricing =
    booking.pricing || booking.bookingDetails?.pricing || {};

  const ticketPrice = toNum(booking.ticketPrice ?? nestedPricing.ticketPrice);
  const snacksAmount = toNum(
    booking.snacksAmount ??
      booking.snacksPrice ??
      nestedPricing.snacksAmount ??
      nestedPricing.snacksPrice,
  );
  const snacksGST = toNum(booking.snacksGST ?? nestedPricing.snacksGST);
  const snacksBase = snacksAmount - snacksGST;
  const snacksGstPercent =
    snacksGST > 0 && snacksBase > 0
      ? Math.round((snacksGST / snacksBase) * 100)
      : null;
  const convenienceFeeBase = toNum(
    booking.convenienceFeeBase ?? nestedPricing.convenienceFeeBase,
  );
  const convenienceFeeGST = toNum(
    booking.convenienceFeeGST ?? nestedPricing.convenienceFeeGST,
  );
  const convenienceFeeTotal = convenienceFeeBase + convenienceFeeGST;
  const couponDiscount = toNum(
    booking.couponDiscount ?? nestedPricing.couponDiscount,
  );
  const couponCode = booking.couponCode || nestedPricing.couponCode || null;
  const promotionalItems: Array<{ name: string; qty: number; unitPrice: number }> =
    (Array.isArray(booking.promotionalItems) && booking.promotionalItems) ||
    (Array.isArray(booking.bookingDetails?.promotionalItems) &&
      booking.bookingDetails.promotionalItems) ||
    [];
  const totalAmount = toNum(
    booking.amountValue ??
      booking.amount ??
      booking.paidAmount ??
      nestedPricing.totalAmount,
  );

  const seatCount = booking.seatCount || (booking.seats?.length ?? 0) || 1;

  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
    placement: "left" | "right";
  }>({ top: 0, left: 0, placement: "left" });

  useEffect(() => {
    if (!anchorRect || !popoverRef.current) return;
    const popRect = popoverRef.current.getBoundingClientRect();
    const popWidth = popRect.width || 320;
    const popHeight = popRect.height || 280;
    const margin = 12;

    // Try left first
    const leftX = anchorRect.left - popWidth - margin;
    const rightX = anchorRect.right + margin;

    const fitsLeft = leftX >= 0;
    const fitsRight = rightX + popWidth <= window.innerWidth;

    let placement: "left" | "right" = "left";
    let left = leftX;
    if (fitsLeft) {
      placement = "left";
      left = leftX;
    } else if (fitsRight) {
      placement = "right";
      left = rightX;
    } else {
      // Fallback: place at edge
      placement = "right";
      left = Math.max(
        margin,
        Math.min(rightX, window.innerWidth - popWidth - margin),
      );
    }

    // Vertically center on the icon
    let top = anchorRect.top + anchorRect.height / 2 - popHeight / 2;
    if (top < margin) top = margin;
    if (top + popHeight > window.innerHeight - margin) {
      top = window.innerHeight - popHeight - margin;
    }

    setPosition({ top, left, placement });
  }, [anchorRect]);

  const Row = ({
    label,
    value,
    muted = false,
    subtle = false,
  }: {
    label: React.ReactNode;
    value: React.ReactNode;
    muted?: boolean;
    subtle?: boolean;
  }) => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        color: muted ? "#6c757d" : "#212529",
        fontWeight: subtle ? 400 : 500,
        fontSize: subtle ? "0.8rem" : "0.85rem",
        padding: "2px 0",
      }}
    >
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );

  return (
    <div
      ref={popoverRef}
      onMouseDown={(e) => e.stopPropagation()}
      style={{
        position: "fixed",
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 9999,
        width: "320px",
        maxWidth: "calc(100vw - 24px)",
        background: "#fff",
        border: "1px solid #dee2e6",
        borderRadius: "8px",
        boxShadow:
          "0 0.5rem 1rem rgba(0, 0, 0, 0.15), 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)",
        fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
        color: "#212529",
        pointerEvents: "auto",
      }}
    >
      {/* Arrow */}
      <div
        style={
          {
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            [position.placement === "left" ? "right" : "left"]: "-7px",
            width: 0,
            height: 0,
            borderTop: "7px solid transparent",
            borderBottom: "7px solid transparent",
            [position.placement === "left" ? "borderRight" : "borderLeft"]:
              "7px solid #fff",
            filter:
              position.placement === "left"
                ? "drop-shadow(1px 0 0 #dee2e6)"
                : "drop-shadow(-1px 0 0 #dee2e6)",
          } as React.CSSProperties
        }
      />

      {/* Header */}
      <div
        style={{
          padding: "10px 14px",
          background: "linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)",
          color: "#fff",
          borderTopLeftRadius: "8px",
          borderTopRightRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontWeight: 600,
          fontSize: "0.95rem",
        }}
      >
        <span>
          <i className="bi bi-receipt me-2" style={{ color: "#fff" }}></i>
          Price Breakdown
        </span>
        <code
          style={{
            fontSize: "0.75rem",
            background: "rgba(255,255,255,0.18)",
            color: "#fff",
            padding: "2px 8px",
            borderRadius: "4px",
          }}
        >
          {booking.bookingNumber || booking.id}
        </code>
      </div>

      {/* Body */}
      <div style={{ padding: "12px 14px" }}>
        {ticketPrice > 0 && (
          <Row
            label={`Ticket Price (${seatCount} × Tickets)`}
            value={fmt(ticketPrice)}
          />
        )}

        {snacksAmount > 0 && (
          <>
            <Row label="Snacks (incl. GST)" value={fmt(snacksAmount)} />
            {snacksGST > 0 && (
              <div style={{ marginTop: "2px" }}>
                <Row
                  label={
                    <span style={{ paddingLeft: "12px" }}>Base Amount</span>
                  }
                  value={fmt(snacksBase)}
                  subtle
                  muted
                />
                <Row
                  label={
                    <span style={{ paddingLeft: "12px" }}>
                      IGST{snacksGstPercent !== null ? ` @ ${snacksGstPercent}%` : ""}
                    </span>
                  }
                  value={fmt(snacksGST)}
                  subtle
                  muted
                />
              </div>
            )}
          </>
        )}

        {promotionalItems.length > 0 && (
          <Row
            label={
              <span style={{ color: "#28a745" }}>
                🎁 {promotionalItems[0].name} (Website Launch Offer)
              </span>
            }
            value={<span style={{ color: "#28a745" }}>FREE</span>}
          />
        )}

        {convenienceFeeTotal > 0 && (
          <div style={{ marginTop: snacksAmount > 0 ? "8px" : "0" }}>
            <Row
              label="Convenience Fee (incl. GST)"
              value={fmt(convenienceFeeTotal)}
            />
            {convenienceFeeBase > 0 && (
              <Row
                label={<span style={{ paddingLeft: "12px" }}>Base Amount</span>}
                value={fmt(convenienceFeeBase)}
                subtle
                muted
              />
            )}
            {convenienceFeeGST > 0 && (
              <Row
                label={<span style={{ paddingLeft: "12px" }}>IGST @ 18%</span>}
                value={fmt(convenienceFeeGST)}
                subtle
                muted
              />
            )}
          </div>
        )}

        {couponDiscount > 0 && (
          <Row
            label={
              <span style={{ color: "#198754" }}>
                Coupon Discount
                {couponCode ? ` (${couponCode})` : ""}
              </span>
            }
            value={
              <span style={{ color: "#198754" }}>- {fmt(couponDiscount)}</span>
            }
          />
        )}

        <hr style={{ margin: "8px 0", borderColor: "#e9ecef" }} />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontWeight: 700,
            fontSize: "0.95rem",
          }}
        >
          <span>Total Amount</span>
          <span style={{ color: "#198754" }}>{fmt(totalAmount)}</span>
        </div>
      </div>
    </div>
  );
};

AmountBreakdownPopover.displayName = "AmountBreakdownPopover";

const Bookings: React.FC = () => {
  const [bookings, setBookings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<any>(null);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalBookings, setTotalBookings] = useState(0);
  const [selectedBookings, setSelectedBookings] = useState<Set<string>>(
    new Set(),
  );
  const [processingRefund, setProcessingRefund] = useState<string | null>(null);
  const [processingBulkRefund, setProcessingBulkRefund] = useState(false);
  const [processingCancel, setProcessingCancel] = useState<string | null>(null);
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [showManualRefundModal, setShowManualRefundModal] = useState(false);
  const [manualRefundBooking, setManualRefundBooking] = useState<any>(null);
  const [cancellationBooking, setCancellationBooking] = useState<any>(null);
  const [cancellationType, setCancellationType] = useState<"single" | "bulk">(
    "single",
  );
  const [exporting, setExporting] = useState(false);
  const [period, setPeriod] = useState<PeriodValue>({ period: "this_week" });
  const itemsPerPage = 10;
  const [actionType, setActionType] = useState<"cancel" | "refund">("cancel");

  const [hoveredBooking, setHoveredBooking] = useState<any | null>(null);
  const [hoverAnchor, setHoverAnchor] = useState<DOMRect | null>(null);
  const hideTimerRef = useRef<number | null>(null);

  const handleIconEnter = (
    e: React.MouseEvent<HTMLSpanElement>,
    booking: any,
  ) => {
    if (hideTimerRef.current) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setHoverAnchor(rect);
    setHoveredBooking(booking);
  };

  const handleIconFocus = (
    e: React.FocusEvent<HTMLSpanElement>,
    booking: any,
  ) => {
    if (hideTimerRef.current) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setHoverAnchor(rect);
    setHoveredBooking(booking);
  };

  const handleIconLeave = () => {
    if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
    hideTimerRef.current = window.setTimeout(() => {
      setHoveredBooking(null);
      setHoverAnchor(null);
    }, 150);
  };

  const handlePopoverEnter = () => {
    if (hideTimerRef.current) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  const handlePopoverLeave = () => {
    if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
    hideTimerRef.current = window.setTimeout(() => {
      setHoveredBooking(null);
      setHoverAnchor(null);
    }, 150);
  };

  useEffect(() => {
    loadBookings();
  }, [currentPage, filterStatus, period]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage === 1) {
        loadBookings();
      } else {
        setCurrentPage(1);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [currentPage]);

  const loadBookings = async () => {
    try {
      setLoading(true);
      const periodDateRange =
        period.period === "custom" && period.dateFrom && period.dateTo
          ? { dateFrom: period.dateFrom, dateTo: period.dateTo }
          : null;

      const response = await adminService.getBookings({
        search: searchTerm,
        status: filterStatus === "all" ? undefined : filterStatus,
        page: currentPage,
        limit: itemsPerPage,
        sortBy: "created_at",
        sortOrder: "desc",
        period: periodDateRange ? "custom" : period.period,
        dateFrom: periodDateRange?.dateFrom,
        dateTo: periodDateRange?.dateTo,
      });

      if (response.success && response.data) {
        setBookings(response.data);
        if (response.pagination) {
          setTotalPages(response.pagination.totalPages);
          setTotalBookings(Number(response.pagination.total) || 0);
        }
      }
    } catch (error: any) {
      console.error("Error loading bookings:", error);
      toast.error("Failed to load bookings");
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleExportBookings = async () => {
    try {
      setExporting(true);

      const periodDateRange =
        period.period === "custom" && period.dateFrom && period.dateTo
          ? { dateFrom: period.dateFrom, dateTo: period.dateTo }
          : null;

      await adminService.exportBookings({
        search: searchTerm,
        status: filterStatus === "all" ? undefined : filterStatus,
        period: periodDateRange ? "custom" : period.period,
        dateFrom: periodDateRange?.dateFrom,
        dateTo: periodDateRange?.dateTo,
      });

      toast.success("Bookings exported successfully");
    } catch (error: any) {
      toast.error(error.message || "Failed to export bookings");
    } finally {
      setExporting(false);
    }
  };

  const handleRefundBooking = async (booking: any) => {
    setCancellationBooking(booking);
    setCancellationType("single");
    setActionType("refund");
    setShowCancellationModal(true);
  };

  const handleCancelBooking = async (booking: any) => {
    setCancellationBooking(booking);
    setCancellationType("single");
    setActionType("cancel");
    setShowCancellationModal(true);
  };

  const handleConfirmCancellation = async (
    reason: string,
    extra?: { cancellationCategory?: "my_cinemas_issue" | "customer_initiated" },
  ) => {
    setShowCancellationModal(false);

    const cancellationCategory = extra?.cancellationCategory || "my_cinemas_issue";

    if (cancellationType === "single" && cancellationBooking) {
      try {
        if (actionType === "refund") {
          setProcessingRefund(cancellationBooking.id);

          const response = await adminService.refundBooking(
            cancellationBooking.id,
            reason,
            cancellationCategory,
          );

          if (response.success) {
            toast.success(
              response.data?.message || "Booking refunded successfully!",
            );
            loadBookings();
            setSelectedBookings(new Set());
          } else {
            toast.error(response.error || "Failed to refund booking");
          }
        } else {
          setProcessingCancel(cancellationBooking.id);

          const response = await adminService.cancelBooking(
            cancellationBooking.id,
            reason,
            cancellationCategory,
          );

          if (response.success) {
            toast.success(
              response.data?.message || "Booking cancelled successfully!",
            );
            loadBookings();
            setSelectedBookings(new Set());
          } else {
            toast.error(response.error || "Failed to cancel booking");
          }
        }

        // const isRefund = canRefund(cancellationBooking);
        // if (isRefund) {
        //   setProcessingRefund(cancellationBooking.id);
        //   const response = await adminService.refundBooking(
        //     cancellationBooking.id,
        //     reason,
        //   );
        //   if (response.success) {
        //     toast.success(
        //       response.data?.message || "Booking refunded successfully!",
        //     );
        //     loadBookings();
        //     setSelectedBookings(new Set());
        //   } else {
        //     toast.error(response.error || "Failed to refund booking");
        //   }
        // } else {
        //   setProcessingCancel(cancellationBooking.id);
        //   const response = await adminService.cancelBooking(
        //     cancellationBooking.id,
        //     reason,
        //   );
        //   if (response.success) {
        //     toast.success(
        //       response.data?.message || "Booking cancelled successfully!",
        //     );
        //     loadBookings();
        //     setSelectedBookings(new Set());
        //   } else {
        //     toast.error(response.error || "Failed to cancel booking");
        //   }
        // }
      } catch (error: any) {
        toast.error(
          error.response?.data?.error ||
            `Failed to ${canRefund(cancellationBooking) ? "refund" : "cancel"} booking`,
        );
      } finally {
        setProcessingRefund(null);
        setProcessingCancel(null);
        setCancellationBooking(null);
      }
    } else if (cancellationType === "bulk") {
      try {
        setProcessingBulkRefund(true);
        const bookingIds = Array.from(selectedBookings);
        const response = await adminService.refundBookingsBulk(
          bookingIds,
          reason,
        );
        if (response.success) {
          toast.success(
            response.message ||
              `Successfully processed ${response.data?.successful || 0} refund(s)`,
          );
          loadBookings();
          setSelectedBookings(new Set());
        } else {
          toast.error(response.error || "Failed to process bulk refund");
        }
      } catch (error: any) {
        toast.error(
          error.response?.data?.error || "Failed to process bulk refund",
        );
      } finally {
        setProcessingBulkRefund(false);
      }
    }
  };

  const handleCancelFullShow = async (booking: any) => {
    try {
      const confirm = window.confirm(
        "Are you sure? This will cancel ALL bookings for this show.",
      );
      if (!confirm) return;

      const showId = booking.myvistaSessionId;
      const cinemaId = booking.cinemaId;
      const showDateTime = booking.showDateTime;

      console.log({ showId, cinemaId, showDateTime }); // debug

      if (!showId || !cinemaId || !showDateTime) {
        toast.error("Missing show details");
        return;
      }

      setProcessingBulkRefund(true);

      const response = await adminService.cancelShow(
        showId,
        cinemaId,
        showDateTime,
        "Show cancelled by admin",
      );

      if (response.success) {
        toast.success("Full show cancellation started");
        loadBookings();
      } else {
        toast.error(response.error || "Failed to cancel show");
      }
    } catch (err: any) {
      toast.error(err.message || "Error cancelling show");
    } finally {
      setProcessingBulkRefund(false);
    }
  };
  const handleBulkRefund = async () => {
    if (selectedBookings.size === 0) {
      toast.error("Please select at least one booking to refund");
      return;
    }

    setCancellationType("bulk");
    setShowCancellationModal(true);
  };

  const handleSelectBooking = (bookingId: string) => {
    const newSelected = new Set(selectedBookings);
    if (newSelected.has(bookingId)) {
      newSelected.delete(bookingId);
    } else {
      newSelected.add(bookingId);
    }
    setSelectedBookings(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedBookings.size === filteredBookings.length) {
      setSelectedBookings(new Set());
    } else {
      setSelectedBookings(new Set(filteredBookings.map((b) => b.id)));
    }
  };

  const canRefund = (booking: any) => {
    const status = booking.status?.toLowerCase();

    if (status !== "confirmed" && status !== "completed") return false;

    if (!booking.showDate || !booking.showTime) return false;

    return new Date(`${booking.showDate} ${booking.showTime}`) > new Date();
  };

  const canCancel = (booking: any) => {
    const status = booking.status?.toLowerCase();

    // confirmed and completed get Refund button instead
    if (
      status === "confirmed" ||
      status === "completed" ||
      status === "cancelled"
    )
      return false;

    if (!booking.showDate || !booking.showTime) return false;

    return new Date(`${booking.showDate} ${booking.showTime}`) > new Date();
  };

  const filteredBookings = bookings;

  const refundableBookings = filteredBookings.filter(
    (b) =>
      selectedBookings.has(b.id) &&
      b.status?.toLowerCase() === "completed" &&
      b.paymentStatus?.toLowerCase() === "completed",
  );

  const getStatusBadge = (status: string) => {
    const variants: Record<string, string> = {
      confirmed: "success",
      pending: "warning",
      cancelled: "danger",
      completed: "info",
    };
    return <Badge bg={variants[status] || "secondary"}>{status}</Badge>;
  };

  const getPaymentStatusBadge = (status: string) => {
    const variants: Record<string, string> = {
      completed: "success",
      pending: "warning",
      failed: "danger",
      refunded: "info",
    };
    return <Badge bg={variants[status] || "secondary"}>{status}</Badge>;
  };

  const handleViewDetails = (booking: any) => {
    setSelectedBooking(booking);
    setShowDetailsModal(true);
  };

  return (
    <>
      <Helmet>
        <title>Bookings - MyCinemas Admin Panel</title>
      </Helmet>

      <div className="fade-in">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="mb-1">Bookings</h2>
            <p className="text-muted mb-0">
              Manage movie bookings and reservations
            </p>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {/* <DateRangeFilter
              value={period}
              onChange={(p) => {
                setPeriod(p);
                setCurrentPage(1);
              }}
              showAll={false}
            /> */}
            <Button
              variant="outline-success"
              onClick={handleExportBookings}
              disabled={exporting}
            >
              {exporting ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  Exporting...
                </>
              ) : (
                <>
                  <i className="bi-download me-1"></i>
                  Export Report
                </>
              )}
            </Button>

            {false && refundableBookings.length > 0 && (
              <Button
                variant="danger"
                onClick={handleBulkRefund}
                disabled={processingBulkRefund}
              >
                <i className="bi-arrow-counterclockwise me-1"></i>
                Refund Selected ({refundableBookings.length})
              </Button>
            )}

            <Button variant="primary" onClick={() => setShowAddModal(true)}>
              <i className="bi-plus-circle me-1"></i>
              New Booking
            </Button>
          </div>
        </div>

        <Card>
          <Card.Header>
            <Row className="align-items-center">
              <Col>
                <h5 className="mb-0">All Bookings ({totalBookings})</h5>
              </Col>
              <Col md={3}>
                <Form.Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="confirmed">Confirmed</option>
                  {/* <option value="pending">Pending</option> */}
                  <option value="cancelled">Cancelled</option>
                  {/* <option value="completed">Completed</option> */}
                </Form.Select>
              </Col>
              <Col md={4}>
                <InputGroup>
                  <InputGroup.Text>
                    <i className="bi-search"></i>
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search by booking ID, movie, cinema, customer name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
            </Row>
          </Card.Header>
          <Card.Body className="p-0">
            <Table responsive hover className="mb-0">
              <colgroup>
                <col style={{ width: "40px" }} /> {/* checkbox */}
                <col style={{ width: "120px" }} /> {/* booking id */}
                <col style={{ width: "240px" }} /> {/* customer + phone */}
                <col style={{ width: "200px" }} /> {/* movie */}
                <col style={{ width: "140px" }} /> {/* cinema */}
                <col style={{ width: "130px" }} /> {/* show date */}
                <col style={{ width: "160px" }} /> {/* seats */}
                <col style={{ width: "160px" }} /> {/* amount breakdown */}
                <col style={{ width: "100px" }} /> {/* status */}
                <col style={{ width: "110px" }} /> {/* payment */}
                <col style={{ width: "80px" }} /> {/* actions */}
              </colgroup>
              <thead className="table-light">
                <tr>
                  <th>
                    <Form.Check
                      type="checkbox"
                      checked={
                        selectedBookings.size === filteredBookings.length &&
                        filteredBookings.length > 0
                      }
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>Booking ID</th>
                  <th>Customer</th>
                  <th>Movie</th>
                  <th>Cinema</th>
                  <th>
                    <div style={{ lineHeight: "1.2" }}>
                      Show Date
                      <br />& Time
                    </div>
                  </th>
                  <th>Seats</th>
                  <th>Total Amount</th>
                  <th>Status</th>
                  <th>Payment</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredBookings.map((booking) => (
                  <tr key={booking.id}>
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={selectedBookings.has(booking.id)}
                        onChange={() => handleSelectBooking(booking.id)}
                      />
                    </td>
                    <td>
                      <code>{booking.bookingNumber || booking.id}</code>
                    </td>
                    <td>
                      <div>
                        <div className="fw-medium">{booking.customer}</div>
                        {booking.customerEmail && (
                          <div
                            className="d-flex align-items-center mt-1"
                            style={{ fontSize: "0.82rem", color: "#6c757d" }}
                          >
                            <i
                              className="bi-envelope me-1"
                              style={{ color: "#0d6efd" }}
                            ></i>
                            <span>{booking.customerEmail}</span>
                          </div>
                        )}
                        {booking.customerMobile && (
                          <div className="d-flex align-items-center mt-1">
                            <i
                              className="bi-telephone-fill me-1"
                              style={{ color: "#198754", fontSize: "0.85rem" }}
                            ></i>
                            <a
                              href={`tel:${booking.customerMobile}`}
                              className="fw-semibold text-decoration-none"
                              style={{ color: "#198754", fontSize: "0.85rem" }}
                            >
                              {booking.customerMobile}
                            </a>
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="fw-medium">{booking.movie}</div>
                    </td>
                    <td>{booking.cinema}</td>
                    <td>
                      <div>
                        <div className="fw-medium">{booking.showDate}</div>
                        <small className="text-muted">{booking.showTime}</small>
                      </div>
                    </td>
                    <td style={{ maxWidth: "200px" }}>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "4px",
                          lineHeight: "1.2",
                        }}
                      >
                        {booking.seats.map((seat: string, index: number) => (
                          <Badge key={index} bg="light" text="dark">
                            {seat}
                          </Badge>
                        ))}
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <span className="fw-bold" style={{ fontSize: "1rem" }}>
                          {booking.amount}
                        </span>
                        <span
                          onMouseEnter={(e) => handleIconEnter(e, booking)}
                          onMouseLeave={handleIconLeave}
                          onFocus={(e) => handleIconFocus(e, booking)}
                          onBlur={handleIconLeave}
                          style={{
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                          }}
                          tabIndex={0}
                          role="button"
                          aria-label="View price breakdown"
                        >
                          <i
                            className="bi bi-info-circle-fill text-primary"
                            style={{ fontSize: "1rem" }}
                          ></i>
                        </span>
                      </div>
                    </td>
                    <td>{getStatusBadge(booking.status)}</td>
                    <td>
                      {getPaymentStatusBadge(booking.paymentStatus)}
                      {booking.paymentMethod && (
                        <div className="mt-1">
                          <Badge
                            bg="info"
                            style={{ fontSize: "0.7rem", fontWeight: 600 }}
                          >
                            {String(booking.paymentMethod).toUpperCase()}
                          </Badge>
                        </div>
                      )}
                    </td>
                    <td>
                      <Dropdown>
                        <Dropdown.Toggle
                          variant="outline-secondary"
                          size="sm"
                          id={`dropdown-${booking.id}`}
                        >
                          <i className="bi-three-dots"></i>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item
                            onClick={() => handleViewDetails(booking)}
                          >
                            <i className="bi-eye me-2"></i>View Details
                          </Dropdown.Item>
                          {/* <Dropdown.Item>
                            <i className="bi-pencil me-2"></i>Edit Booking
                          </Dropdown.Item> */}
                          {/* <Dropdown.Divider /> */}

                          {canRefund(booking) && (
                            <Dropdown.Item
                              className="text-danger"
                              onClick={() => handleRefundBooking(booking)}
                              disabled={processingRefund === booking.id}
                            >
                              {processingRefund === booking.id ? (
                                <>
                                  <Spinner size="sm" className="me-2" />
                                  Processing...
                                </>
                              ) : (
                                <>
                                  <i className="bi-arrow-counterclockwise me-2"></i>
                                  Refund and Cancel
                                </>
                              )}
                            </Dropdown.Item>
                          )}

                          {canCancel(booking) && (
                            <Dropdown.Item
                              className="text-warning"
                              onClick={() => handleCancelBooking(booking)}
                              disabled={processingCancel === booking.id}
                            >
                              {processingCancel === booking.id ? (
                                <>
                                  <Spinner size="sm" className="me-2" />
                                  Processing...
                                </>
                              ) : (
                                <>
                                  <i className="bi-x-circle me-2"></i>
                                  Cancel
                                </>
                              )}
                            </Dropdown.Item>
                          )}

                          {/* Manual Refund (cash at counter) — for confirmed/completed bookings */}
                          {(booking.status === "confirmed" ||
                            booking.status === "completed") && (
                            <Dropdown.Item
                              className="text-success"
                              onClick={() => {
                                setManualRefundBooking(booking);
                                setShowManualRefundModal(true);
                              }}
                            >
                              <i className="bi-cash-coin me-2"></i>
                              Manual Refund
                            </Dropdown.Item>
                          )}
                          {canRefund(booking) && (
                            <>
                              <Dropdown.Divider />

                              <Dropdown.Item
                                className="text-danger fw-bold"
                                onClick={() => handleCancelFullShow(booking)}
                                disabled={processingBulkRefund}
                              >
                                {processingBulkRefund ? (
                                  <>
                                    <Spinner size="sm" className="me-2" />
                                    Processing...
                                  </>
                                ) : (
                                  <>
                                    <i className="bi-x-octagon me-2"></i>
                                    Refund & Cancel Full Show
                                  </>
                                )}
                              </Dropdown.Item>
                            </>
                          )}
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
          {totalPages > 1 && (
            <Card.Footer className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3 py-3">
              <div className="text-muted small mb-0">
                {totalBookings > 0
                  ? `Showing ${(currentPage - 1) * itemsPerPage + 1}-${Math.min(currentPage * itemsPerPage, totalBookings)} of ${totalBookings} bookings`
                  : "No bookings found"}
              </div>
              <Pagination className="mb-0">
                <Pagination.Prev
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                />
                {Array.from(
                  { length: totalPages },
                  (_, index) => index + 1,
                ).map((pageNumber) => (
                  <Pagination.Item
                    key={pageNumber}
                    active={pageNumber === currentPage}
                    onClick={() => setCurrentPage(pageNumber)}
                  >
                    {pageNumber}
                  </Pagination.Item>
                ))}
                <Pagination.Next
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                />
              </Pagination>
            </Card.Footer>
          )}
        </Card>

        {/* Booking Details Modal */}
        <BookingDetailsModal
          show={showDetailsModal}
          onHide={() => setShowDetailsModal(false)}
          booking={selectedBooking}
        />

        {/* Add Booking Modal */}
        <AddBookingModal
          show={showAddModal}
          onHide={() => setShowAddModal(false)}
          onSuccess={() => {
            loadBookings();
          }}
        />

        {/* Cancellation Reason Modal */}
        <CancellationReasonModal
          show={showCancellationModal}
          onHide={() => {
            setShowCancellationModal(false);
            setCancellationBooking(null);
          }}
          onConfirm={handleConfirmCancellation}
          title={
            cancellationType === "bulk"
              ? "Bulk Refund - Cancellation Reason"
              : actionType === "refund"
                ? "Refund Booking - Cancellation Reason"
                : "Cancel Booking - Cancellation Reason"
          }
          message={
            cancellationType === "single" && cancellationBooking
              ? `Are you sure you want to ${actionType === "refund" ? "refund" : "cancel"}booking #${cancellationBooking.bookingNumber || cancellationBooking.id}${cancellationBooking.myvistaBookingId ? ` (Vista: ${cancellationBooking.myvistaBookingId})` : ""}? This will ${actionType === "refund" ? "cancel the booking in Vista and process a refund" : "cancel the booking"}.`
              : cancellationType === "bulk"
                ? `Are you sure you want to refund ${selectedBookings.size} booking(s)? This will cancel the bookings in Vista and process refunds.`
                : undefined
          }
          bookingCount={
            cancellationType === "bulk" ? selectedBookings.size : undefined
          }
          isLoading={
            cancellationType === "single" && cancellationBooking
              ? cancellationBooking && canRefund(cancellationBooking)
                ? processingRefund === cancellationBooking.id
                : processingCancel === cancellationBooking.id
              : false
          }
          showCategory={cancellationType === "single"}
        />

        {/* Manual Refund (counter cash) Modal */}
        <ManualRefundModal
          show={showManualRefundModal}
          onHide={() => {
            setShowManualRefundModal(false);
            setManualRefundBooking(null);
          }}
          booking={manualRefundBooking}
          onSuccess={() => loadBookings()}
        />

        {/* Amount Breakdown Hover Popover */}
        {hoveredBooking && (
          <div
            onMouseEnter={handlePopoverEnter}
            onMouseLeave={handlePopoverLeave}
          >
            <AmountBreakdownPopover
              booking={hoveredBooking}
              anchorRect={hoverAnchor}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default Bookings;
